import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, FilterChip, Input, PageHeader, QueryState, Select } from '@yhelp/ui';
import type {
  Ticket,
  TicketClassification,
  TicketPriority,
  TicketProduct,
  TicketStatus,
} from '@yhelp/shared-types';
import { TicketListTable } from '@/components/organisms/TicketListTable';
import { DashboardLayout, PrimaryLinkButton } from '@/templates/DashboardLayout';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';
import {
  YSEC_CLASSIFICATIONS,
  YSEC_PRIORITIES,
  YSEC_PRODUCTS,
  YSEC_STATUSES,
  filterTicketsAdvanced,
  hasActiveFilters,
} from '@/lib/ysec-catalog';

export function TicketsPage() {
  const authorizedFetch = useAuthorizedFetch();
  const [statusFilter, setStatusFilter] = useState<'' | TicketStatus>('');
  const [priorityFilter, setPriorityFilter] = useState<'' | TicketPriority>('');
  const [productFilter, setProductFilter] = useState<'' | TicketProduct>('');
  const [classificationFilter, setClassificationFilter] = useState<'' | TicketClassification>('');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tickets', statusFilter, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('q', search.trim());
      const qs = params.toString();
      return authorizedFetch<{ data: Ticket[] }>(`/v1/tickets${qs ? `?${qs}` : ''}`);
    },
  });

  const filtered = useMemo(() => {
    const list = data?.data ?? [];
    return filterTicketsAdvanced(list, {
      priority: priorityFilter,
      product: productFilter,
      classification: classificationFilter,
      search,
    });
  }, [data?.data, priorityFilter, productFilter, classificationFilter, search]);

  const filtersActive = hasActiveFilters({
    priority: priorityFilter,
    product: productFilter,
    classification: classificationFilter,
    search,
  });
  const hasAnyFilter = Boolean(statusFilter || filtersActive);
  const isEmpty = !isLoading && !isError && filtered.length === 0;

  const clearFilters = () => {
    setStatusFilter('');
    setPriorityFilter('');
    setProductFilter('');
    setClassificationFilter('');
    setSearch('');
  };

  return (
    <DashboardLayout
      pageDescription="Gestão corporativa de chamados — portfólio ySec"
      primaryAction={
        <span data-tour="ticket-create-btn">
          <PrimaryLinkButton to="/tickets/new">Novo chamado</PrimaryLinkButton>
        </span>
      }
    >
      <PageHeader
        title="Chamados"
        description="Filtre por status, produto, prioridade ou classificação ITIL"
      />

      <div className="mb-4 space-y-3" data-tour="tickets-list">
        <div className="flex flex-wrap gap-2">
          {YSEC_STATUSES.map((f) => (
            <FilterChip
              key={f.label}
              selected={statusFilter === f.value}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </FilterChip>
          ))}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Select
              aria-label="Filtrar por produto ySec"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value as '' | TicketProduct)}
            >
              <option value="">Todos os produtos</option>
              {YSEC_PRODUCTS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Filtrar por prioridade"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as '' | TicketPriority)}
            >
              {YSEC_PRIORITIES.map((p) => (
                <option key={p.label} value={p.value}>
                  {p.label === 'Todas' ? 'Todas prioridades' : p.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Filtrar por classificação"
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e.target.value as '' | TicketClassification)}
            >
              {[{ value: '' as const, label: 'Todas classificações' }, ...YSEC_CLASSIFICATIONS].map(
                (c) => (
                  <option key={c.value || 'all'} value={c.value}>
                    {c.label}
                  </option>
                ),
              )}
            </Select>
          </div>
          <Input
            className="w-full lg:max-w-xs"
            placeholder="Buscar por título ou número…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar chamados"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted">
          <span>
            {isLoading
              ? 'Carregando…'
              : `${filtered.length} chamado${filtered.length !== 1 ? 's' : ''}`}
          </span>
          {hasAnyFilter ? (
            <Button size="sm" variant="ghost" onClick={clearFilters}>
              Limpar filtros
            </Button>
          ) : null}
        </div>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty}
        errorProps={{
          title: 'Erro ao carregar chamados',
          action: (
            <Button variant="secondary" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          ),
        }}
        emptyProps={{
          variant: hasAnyFilter ? 'search' : 'default',
          title: hasAnyFilter ? 'Nenhum resultado' : 'Nenhum chamado',
          description: hasAnyFilter
            ? 'Ajuste os filtros ou limpe a busca.'
            : 'Crie o primeiro chamado para começar.',
          action: hasAnyFilter ? (
            <Button variant="secondary" onClick={clearFilters}>
              Limpar filtros
            </Button>
          ) : (
            <PrimaryLinkButton to="/tickets/new">Novo chamado</PrimaryLinkButton>
          ),
        }}
      >
        <TicketListTable tickets={filtered} />
      </QueryState>
    </DashboardLayout>
  );
}
